<script setup lang="ts">
import { useStore } from "vuex";
import { computed, ref } from "vue";
import ButtonPrimitive from "@/components/UI/ButtonPrimitive.vue";
import TextLabel from "@/components/UI/TextLabel.vue";
import EditableItem from './EditableItem.vue';
import MeasurementsHeightTransition from '@/components/UI/MeasurementsHeightTransition.vue';
import { AbstractPreset } from "@/presets/AbstractPreset";

export interface Props {
	disabledShapes?: string[];
	storeShapeGetter?: string;
	storeShapeUpdater?: string;
	action?: string;
	prefix?: string;
	isCutout?: boolean;
	modifierAction?: string;
}

const i18n = window.measurementSelectorVariables.i18n;
const store = useStore();
const props = defineProps<Props>();
const type = ref('straight');

const presetShape = computed(() => store.getters.presetShape);
const currentShape = computed(() => {
	if (!props.storeShapeGetter) return;

	// We don't have presets for cutouts so we don't need to check for them
	if (props.isCutout) return store.getters[props.storeShapeGetter];

	if (presetShape.value) {
		setCategory('preset');
		return presetShape.value;
	}

	return store.getters[props.storeShapeGetter];
});
const presets = computed(() => store.getters.getPresets );
const shapeName = computed(() => store.getters.getShapeName );

const categorizedShapes = computed(() => {
	const enabledShapes = store.getters.enabledShapes.filter((currentShape: string) => {
		return ( !props.disabledShapes.includes(currentShape) && currentShape !== "customized-currentShape" );
	});

	const categories = store.getters.getCategorizedShapes( enabledShapes );

	// Set order based on the order of the currentShape categories in php (ShapePresenter.php)
	return Object.keys(i18n.shapeCategories).reduce((acc, key) => {
		if (categories[key]) {
			acc[key] = categories[key];
		}
		return acc;
	}, {});
} );

function updateShape(currentShape: string) {
	store.commit( 'updatePresetShape', '' );

	if (props.storeShapeUpdater) {
		store.dispatch(props.storeShapeUpdater, currentShape);
	}
}

function setCategory(category: string) {
	type.value = category;
}

function editShape(): void {
	updateShape('');
}

function updatePreset(preset: AbstractPreset): void {
	store.dispatch('loadPresetConfig', preset);
}

</script>

<template>
	<div>
		<measurements-height-transition appear mode="out-in">
			<div v-if="!currentShape" >
				<ul class="currentShape-type__buttons">
					<li v-for="(shapes, category) in categorizedShapes"
						:key="category"
					>
						<button
							:class="['currentShape-type__button', type === category ? 'currentShape-type__button--active' : '']"
							@click.prevent="setCategory(category)"
						>
							{{ i18n.shapeCategories[category] ?? "Other" }}
						</button>
					</li>
					<li v-if="!props.isCutout">
						<button
							:class="['currentShape-type__button', type === 'preset' ? 'currentShape-type__button--active' : '']"
							@click.prevent="setCategory('preset')"
						>
							{{ i18n.shapeCategories['preset'] }}
						</button>
					</li>
				</ul>
				<text-label class="screen-reader-text">{{ i18n.shapeCategories[type] ?? "Other"}}</text-label>
				<ul class="currentShape-type__list" v-if="categorizedShapes[type]">
					<li v-for="item in categorizedShapes[type]"
						:key="item"
					>
						<button-primitive
							:on-click="() => updateShape(item)"
							:icon="item"
							:class="{
								'button--inactive': item !== '' && item !== currentShape,
							}"
							:aria-label="i18n.shapeNames[item]"
						/>
					</li>
				</ul>
				<ul class="currentShape-type__list" v-if="type === 'preset' && !isCutout">
					<li v-for="preset in presets"
						:key="preset.getName()"
					>
						<button-primitive
							:on-click="() => updatePreset(preset)"
							:icon="preset.getName()"
							:class="{
								'button--inactive': preset.getName() !== '' && preset.getName() !== currentShape,
							}"
							:aria-label="i18n.shapePresets[preset.getName()]"
						/>
					</li>
				</ul>
			</div>
		</measurements-height-transition>

		<measurements-height-transition appear mode="out-in">
			<div v-if="currentShape !== ''" class="editable-list">
				<div class="editable-list__item editable-list__item--active">
					<EditableItem
						:icon="currentShape"
						:edit="{ index: currentShape, title: $i18n.cutout.edit, disabled: false, onClick: editShape, hidden: false }"
						:remove="{ index: currentShape, title: $i18n.cutout.remove, disabled: false, onClick: editShape, hidden: false }"
						>
						{{ shapeName }}
					</EditableItem>
				</div>
			</div>
		</measurements-height-transition>
	</div>
</template>

<style scoped lang="scss">
	.currentShape-type__list {
		display: flex;
		flex-wrap: wrap;
		gap: 16px;
		margin-top: 16px;
	}

	.currentShape-type__buttons {
		display: flex;
		flex-wrap: wrap;
		column-gap: 10px;
		background-color: $brand-blue-background;
	}

	.currentShape-type__button {
		color: $measurements-preview-blue;
		background-color: transparent;
		font-size: 14px;
		padding: 6px 10px;
		border-radius: 3px;
		font-weight: 700;

		&--active,
		&:focus,
		&:hover {
			background-color: $measurements-preview-blue;
			color: white;
		}
	}

	.button {
		height: 44px;
		width: 44px;
		border-radius: 4px;
		line-height: 0;

		:deep(circle),
		:deep(path),
		:deep(rect),
		:deep(ellipse) {
			fill: $brand-blue-light;
			stroke: $brand-blue-dark;
			stroke-width: 2px;
		}

		&:focus,
		&:hover {
			background-color: $brand-blue-light;

			:deep(circle),
			:deep(path),
			:deep(rect),
			:deep(ellipse) {
				fill: $white;
				stroke: $brand-blue-dark;
			}
		}

		&--active {
			:deep(circle),
			:deep(path),
			:deep(rect),
			:deep(ellipse) {
				fill: $white;
			}
		}

		&--inactive {
			:deep(circle),
			:deep(path),
			:deep(rect),
			:deep(ellipse) {
				fill: $measurements-light-blue;
				stroke: $measurements-dark-blue;
			}
		}
	}
</style>
